import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { useAtomValue, useSetAtom } from "jotai";
import { Button, Tooltip } from "@zapier/design-system-beta";
import {
  closeContextMenusAtom,
  takeSnapshotAtom,
  aiSuggestedEditsAtomFamily,
  aiSuggestedEditsLoadingAtomFamily,
  togglesAtomResettable,
  getEdits,
} from "@zapier/canvas-state";
import { MAX_TITLE_LENGTH } from "@zapier/canvas-constants";

import useUpdateNode from "../../hooks/useUpdateNode";

import styles from "./StepTitle.module.css";

export default function StepTitle({
  nodeId,
  label,
  placeholder,
  isSelected,
  rows = 1,
  canNodeGetAiSuggestion,
  isViewOnlyMode = false,
}: {
  nodeId: string;
  label: string;
  placeholder?: string;
  isSelected: boolean;
  rows?: number;
  canNodeGetAiSuggestion?: boolean;
  isViewOnlyMode?: boolean;
}) {
  const takeSnapshot = useSetAtom(takeSnapshotAtom);
  const updateNodeAndSave = useUpdateNode();
  const [newTitle, setNewTitle] = useState(label);
  const generateSuggestion = useSetAtom(getEdits);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const closeContextMenus = useSetAtom(closeContextMenusAtom);
  const isLoading = useAtomValue(aiSuggestedEditsLoadingAtomFamily(nodeId));
  const toggles = useAtomValue(togglesAtomResettable);

  const placeholderDefault =
    !isViewOnlyMode && canNodeGetAiSuggestion
      ? "What should we build?"
      : "Add a step title";

  const resizeTextarea = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto"; // Reset height to auto before calculating the scrollHeight
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  const isSuggestionsPresent = useAtomValue(aiSuggestedEditsAtomFamily(nodeId));

  useEffect(() => {
    resizeTextarea();
  }, [newTitle]);

  /* This is to show the title change after StepTitleField onBlur in sidebar.
   * The reason we don't update label in the node data directly is because it causes the input to re-render and lose focus. Cursor will keep jumping to the end of the input.
   * */
  useEffect(() => {
    if (label !== newTitle) {
      setNewTitle(label);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [label]);

  const handleClick = useCallback(() => {
    closeContextMenus(); // Closes the context menus when user don't need to use them or else they're forced to delete the node
  }, [closeContextMenus]);

  const handleTitleChange = useCallback(
    (evt: ChangeEvent<HTMLTextAreaElement>) => {
      const newLabel = evt.target.value;
      // We don't want to spam the BE on each keydown, so we update the node without saving
      setNewTitle(newLabel);
    },
    [],
  );

  const saveTitle = useCallback(() => {
    const hasTitleChanged = newTitle !== label;
    if (hasTitleChanged) {
      takeSnapshot();

      updateNodeAndSave(
        {
          data: {
            label: newTitle,
          },
        },
        { shouldSave: true },
      );
    }
  }, [label, newTitle, updateNodeAndSave, takeSnapshot]);

  const handleAiSuggestion = useCallback(() => {
    saveTitle();
    generateSuggestion({ sourceNodeId: nodeId });
  }, [generateSuggestion, nodeId, saveTitle]);

  const handleKeyDown = useCallback(
    (evt: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (!toggles.showSmartNodeSuggest) {
        return;
      }

      if ((evt.metaKey || evt.ctrlKey) && evt.key === "Enter") {
        evt.preventDefault();
        handleAiSuggestion();
      }
    },
    [handleAiSuggestion, toggles.showSmartNodeSuggest],
  );
  /* nodrag class is needed so you can drag to select text while editing instead of dragging the node */
  const className = isSelected ? `nodrag ${styles.textarea}` : styles.textarea;
  return (
    <div className={styles.stepTitle} data-testid="StepTitle">
      <textarea
        ref={textareaRef}
        className={classNames(className, {
          [styles.textareaError]: label?.length === MAX_TITLE_LENGTH,
          [styles.noDragSelect]: !isSelected,
        })}
        value={newTitle}
        readOnly={!isSelected || isViewOnlyMode} // We need this so when you drag a step, we don't go into edit mode without node being selected. And makes it easier to select a step to open the sidebar in view-only mode.
        onClick={handleClick}
        onBlur={saveTitle}
        placeholder={placeholder || placeholderDefault}
        onChange={handleTitleChange}
        maxLength={MAX_TITLE_LENGTH}
        rows={rows}
        onKeyDown={handleKeyDown}
      />
      {!isViewOnlyMode && isSelected && canNodeGetAiSuggestion && (
        <div className={styles.aiSuggestion}>
          {isSuggestionsPresent ? (
            <Button
              isLoading={isLoading}
              size="compact"
              variant="tertiary"
              onPress={handleAiSuggestion}
            >
              ⌘ + Enter
            </Button>
          ) : (
            <Tooltip
              content="'⌘ + Enter' will generate AI suggestions "
              placement="top"
            >
              <Button
                isLoading={isLoading}
                size="compact"
                variant="primary"
                onPress={handleAiSuggestion}
              >
                ⌘ + Enter
              </Button>
            </Tooltip>
          )}
        </div>
      )}
    </div>
  );
}
